
// File: MetricGroupForm.tsx
import React, { useState, useEffect, useMemo } from "react";
import { FaPlus, FaSave, FaEdit, FaTrash } from "react-icons/fa";
import Dialog from "../FormDialog/Dialog";
import MetricGroupISODropdown from '../../DropDowns/MetricGroupISODropdown/MetricGroupISODropdown';
import SearchableList from "../../SearchableList/SearchableList";
import MetricDropdown from "../../DropDowns/MetricDropdown/MetricDropdown";
import { getUserInfo } from "../../../services/authService-sighup";

interface MetricGroupFormProps {
  onCreateMetricGroup: (newGroup: any) => void;
  onSaveChanges: (metricGroupId: number, updatedGroup: any) => void;
  onClearSelection: () => void;
  selectedMetricGroup?: any;
  metricGroups: any[];
  onDirtyChange: (dirty: boolean) => void;
  onModeChange: (mode: "create" | "edit" | "view" | null) => void;
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  onDeleteMetricGroup?: (metricGroupId: number) => void;
  canDelete?: boolean;
}

const MetricGroupForm: React.FC<MetricGroupFormProps> = ({
  onCreateMetricGroup,
  onSaveChanges,
  onClearSelection,
  selectedMetricGroup,
  metricGroups,
  onDirtyChange,
  onModeChange,
  registerTabSwitchGuard,
  onDeleteMetricGroup,
  canDelete = false
}) => {
  const [groupName, setGroupName] = useState("");
  const [iso_code, setIso_code] = useState("");
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [metricsList, setMetricsList] = useState<any[]>([]);
  const [metricGroupId, setMetricGroupId] = useState<number | null>(null);

  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [mode, setMode] = useState<"create" | "edit" | "view" | null>(null);

  const [user, setUser] = useState<{ fullName: string; email: string } | null>(
    null
  );
  const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [activeField, setActiveField] = useState<string | null>(null);

  const [dialog, setDialog] = useState<any>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    cancelButtonText: "",
    onConfirm: () => {},
    onCancel: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      cancelButtonText: "",
      onConfirm: () => {},
      onCancel: () => {},
    });
  };

  const getUniqueMetricNames = (metricRows: any[] = []) => {
    const names = metricRows
      .map((m: any) => m?.metric_name)
      .filter((name: any) => typeof name === "string" && name.trim() !== "");
    return Array.from(new Set(names));
  };

  const availableMetricNames = useMemo(() => {
    return getUniqueMetricNames(metricsList);
  }, [metricsList]);

  useEffect(() => {
    const fetchUserInfo = () => {
      const userInfo = getUserInfo();
      if (userInfo) {
        setUser({
          fullName: userInfo.fullName || "Unknown User",
          email: userInfo.email || "",
        });
      }
    };
    fetchUserInfo();
  }, []);

  const createdBy = user?.email || "Unknown User";
  const modifiedBy = user?.email || "Unknown User";

  // -------- APPLY SELECTED METRIC GROUP ----------
  // useEffect(() => {
  //   if (selectedMetricGroup && !isEditMode) {
  //     setGroupName(selectedMetricGroup.metric_group_name || "");
  //     setIso_code(selectedMetricGroup.iso_code || "");
  //     setMetricGroupId(selectedMetricGroup.metric_group_id);

  //     const metricNames = (selectedMetricGroup.metric_ids || []).map(
  //       (m: any) => m.metric_name
  //     );
  //     setSelectedMetrics(metricNames);

  //     setIsViewMode(true);
  //     setIsEditMode(false);
  //     setMode("view");
  //     setModifiedFields({});
  //     onDirtyChange(false);
  //     onModeChange("view");
  //   } else if (selectedMetricGroup && isEditMode) {
  //     // if parent toggles edit externally — keep current behavior
  //     setGroupName(selectedMetricGroup.metric_group_name || "");
  //     setIso_code(selectedMetricGroup.iso_code || "");
  //     setMetricGroupId(selectedMetricGroup.metric_group_id);

  //     const metricNames = (selectedMetricGroup.metric_ids || []).map(
  //       (m: any) => m.metric_name
  //     );
  //     setSelectedMetrics(metricNames);

  //     setIsViewMode(true);
  //     setIsEditMode(false);
  //     setMode("view");
  //     setModifiedFields({});
  //   }
  // }, [selectedMetricGroup]);
  useEffect(() => {
    // VIEW MODE
    if (selectedMetricGroup && !isEditMode) {
      setGroupName(selectedMetricGroup.metric_group_name || "");
      setIso_code(selectedMetricGroup.iso_code || "");
      setMetricGroupId(selectedMetricGroup.metric_group_id);
  
      const metricNames = getUniqueMetricNames(selectedMetricGroup.metric_ids || []);
      setSelectedMetrics(metricNames);
  
      setIsViewMode(true);
      setIsEditMode(false);
      setMode("view");
      setModifiedFields({});
      onDirtyChange(false);
      onModeChange("view");
    }
  
    // RESET → CREATE MODE
    if (!selectedMetricGroup) {
      setGroupName("");
      setIso_code("");
      setSelectedMetrics([]);
      setMetricGroupId(null);
  
      setIsViewMode(false);
      setIsEditMode(false);
      setMode(null);
  
      setModifiedFields({});
      onDirtyChange(false);
      onModeChange(null);
    }
  }, [selectedMetricGroup]);
  

  // -------- HANDLE SUBMIT ----------
  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!groupName.trim()) {
      setDialog({
        open: true,
        title: "Missing Information",
        message: "Metric Group name is required.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    if (!iso_code) {
      setDialog({
        open: true,
        title: "Missing Information",
        message: "ISO Code is required.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    if (selectedMetrics.length === 0 && mode === "edit") {
      // in edit mode we require at least one metric
      setDialog({
        open: true,
        title: "Missing Information",
        message: "At least one metric must be selected.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    // Check duplicate name
    const nameExists = metricGroups.some((g) => {
      const isSameName =
        g.metric_group_name.trim().toLowerCase() ===
        groupName.trim().toLowerCase();
      const isDifferentId = g.metric_group_id !== metricGroupId;
      return isSameName && isDifferentId;
    });

    if (nameExists) {
      setDialog({
        open: true,
        title: "Duplicate Metric Group Name",
        message: `Metric Group "${groupName}" already exists.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    // Map selected metric_names → metric_ids, collecting ALL ISO variants per name
    const selectedMetricIds = selectedMetrics
      .flatMap((name) =>
        metricsList
          .filter((m) => m.metric_name === name)
          .map((m) => m.metric_id)
      )
      .filter((id) => id !== undefined && id !== null);

    const payload = {
      metric_group_name: groupName,
      iso_code: iso_code,
      metric_ids: selectedMetricIds,
      sys_created_by: createdBy,
      sys_modified_by: modifiedBy,
    };

    if (isEditMode && metricGroupId !== null) {
      setDialog({
        open: true,
        title: "Confirm Edit",
        message: "Save changes to this Metric Group?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          onSaveChanges(metricGroupId, payload);
          resetForm();
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      // CREATE
      setDialog({
        open: true,
        title: "Confirm Create",
        message: "Create this Metric Group?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          onCreateMetricGroup(payload);
          resetForm();
          closeDialog();
        },
        onCancel: closeDialog,
      });
    }
  };

  // -------- RESET FORM ----------
  const resetForm = () => {
    setGroupName("");
    setIso_code("");
    setSelectedMetrics([]);
    setMetricGroupId(null);
    setIsViewMode(false);
    setIsEditMode(false);
    setMode(null);
    setModifiedFields({});
    onDirtyChange(false);
    onModeChange(null);
  };

  // -------- NEW ----------
  const handleNew = (e?: any) => {
    if (e) e.preventDefault();
  
    const hasModifications = Object.keys(modifiedFields).length > 0;
  
    if (isEditMode || hasModifications) {
      setDialog({
        open: true,
        title: "Create New?",
        message: "Discard current changes and create a new Metric Group?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          resetForm();
          onClearSelection();
  
          setMode("create");
          onModeChange("create");
  
          setModifiedFields({});      
          onDirtyChange(false);       
  
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      resetForm();
      onClearSelection();
  
      setMode("create");
      onModeChange("create");
  
      setModifiedFields({});         
      onDirtyChange(false);           
    }
  };
  

  // -------- CANCEL EDIT ----------
  const handleCancel = (e?: any) => {
    if (e) e.preventDefault();
    setDialog({
      open: true,
      title: "Cancel Editing?",
      message: "Discard all changes?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        closeDialog();
        if (selectedMetricGroup) {
          setGroupName(selectedMetricGroup.metric_group_name);
          setIso_code(selectedMetricGroup.iso_code);

          const metricNames = getUniqueMetricNames(selectedMetricGroup.metric_ids || []);
          setSelectedMetrics(metricNames);
        }
        setModifiedFields({});
        setIsEditMode(false);
        setIsViewMode(true);
        setMode("view");
        onDirtyChange(false);
        onModeChange("view");
      },
      onCancel: closeDialog,
    });
  };

  // -------- ENABLE EDIT MODE ----------
  const handleEdit = () => {
    setIsEditMode(true);
    setMode("edit");
  
    setModifiedFields({});     
    onDirtyChange(false);     
  
    onModeChange("edit");
  };
  

  // -------- DELETE ----------
  // const handleDeleteClick = () => {
  //   if (!metricGroupId) return;

  //   if (selectedMetrics && selectedMetrics.length > 0) {
  //     setDialog({
  //       open: true,
  //       title: "Cannot Delete",
  //       message: "Metric group has metrics associated with it and cannot be deleted.",
  //       confirmButtonText: "Close",
  //       onConfirm: closeDialog,
  //     });
  //     return;
  //   }

  //   setDialog({
  //     open: true,
  //     title: "Confirm Delete",
  //     message: "Are you sure you want to delete this metric group?",
  //     confirmButtonText: "Yes",
  //     cancelButtonText: "No",
  //     onConfirm: () => {
  //       if (onDeleteMetricGroup && metricGroupId) onDeleteMetricGroup(metricGroupId);
  //       closeDialog();
  //     },
  //     onCancel: closeDialog,
  //   });
  // };
  const handleDeleteClick = () => {
    if (!metricGroupId) return;
  
    setDialog({
      open: true,
      title: "Confirm Delete",
      message: (
        <>
          Are you sure you want to delete this metric group?
          {selectedMetrics.length > 0 && (
            <div className="mt-2 text-sm text-red-600">
              All associated metrics will be removed first.
            </div>
          )}
        </>
      ),
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        if (onDeleteMetricGroup) {
          onDeleteMetricGroup(metricGroupId);
        }
        closeDialog();
      },
      onCancel: closeDialog,
    });
  };
  

  // compute border classes so blue borders only appear in edit mode
  const getInputClass = (fieldKey: string) => {
    const isModified = !!modifiedFields[fieldKey];
    if (isEditMode) {
      return isModified || activeField === fieldKey
        ? "border-green-500 focus:ring-2 focus:ring-green-500"
        : "border-blue-500";
    }

    // create mode and view mode -> neutral gray border
    return "border-gray-300";
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md font-poppins">
      {dialog.open && <Dialog {...dialog} onClose={closeDialog} />}

      {/* DROPDOWN FETCHES METRICS LIST */}
      <MetricDropdown onMetricsFetched={setMetricsList} />

      <form className="space-y-6">

        {/* BUTTONS — with DELETE when in edit mode */}
        <div className="flex justify-between items-center gap-4 mb-4">
          <div className="flex items-center gap-4">
            {isEditMode && canDelete && (
              <button
                onClick={(e) => { e.preventDefault(); handleDeleteClick(); }}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600"
                type="button"
              >
                <FaTrash className="w-4 h-4" /> Delete
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {!isViewMode && !isEditMode && (
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
              >
                <FaPlus className="w-4 h-4" /> Create
              </button>
            )}

            {isViewMode && !isEditMode && (
              <>
                <button
                  onClick={handleNew}
                  className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
                >
                  <FaPlus className="w-4 h-4" /> New
                </button>

                <button
                  onClick={() => handleEdit()}
                  className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
                >
                  <FaEdit className="w-4 h-4" /> Edit
                </button>
              </>
            )}

            {isEditMode && (
              <>
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
                >
                  <FaSave className="w-4 h-4" /> Save
                </button>

                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* NAME & ISO — same row now */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-gray-700 mb-2 font-medium">
              Metric Group Name<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => {
                setGroupName(e.target.value);
                setModifiedFields((prev) => ({ ...prev, groupName: true }));
                onDirtyChange(true);
              }}
              placeholder="Enter metric group name"
              onFocus={() => setActiveField("groupName")}
              disabled={!isEditMode && isViewMode}
              className={`w-full p-3 border rounded-lg bg-gray-50 text-gray-800 shadow-inner text-sm ${getInputClass("groupName")}`}
            />
          </div>

          <div className="w-56">
            <label className="block text-gray-700 mb-2 font-medium">
              ISO Code<span className="text-red-500 ml-1">*</span>
            </label>

            <MetricGroupISODropdown
              selectedISO={iso_code}
              onISOChange={(iso) => {
                setIso_code(iso);
                onDirtyChange(true);
                setModifiedFields(prev => ({ ...prev, iso_code: true }));
              }}
              onFocus={() => setActiveField("iso_code")}
              // disabled={(!isEditMode && isViewMode) || mode === "create"}
              disabled={isViewMode && !isEditMode}
              className={`w-full p-1 border rounded-lg shadow-inner text-sm ${getInputClass("iso_code")}`}
            />
          </div>
        </div>

        {/* METRICS LIST */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Select Metrics<span className="text-red-500 ml-1">*</span>
          </label>

          <div className="bg-gray-50 p-10 rounded-md border border-gray-300">
            <SearchableList
              items={availableMetricNames}
              selectedItems={selectedMetrics}
              preserveItemOrder={false}
              reorderSelectedOnOutside={true}
              onSelectionChange={(sel: string[]) => {
                setSelectedMetrics(Array.from(new Set(sel)));
                setModifiedFields((prev) => ({ ...prev, metrics: true }));
                onDirtyChange(true);
              }}
              // DISABLED while creating — we don't send metrics on create
              disabled={((!isEditMode && isViewMode) || mode === "create" || (!selectedMetricGroup))}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default MetricGroupForm;
